/**
 * Форматтер телеметрии боя
 * Вынесено из legacy-app.js для модульности
 */

/**
 * Форматирует имя события телеметрии
 * Удаляет префиксы "effect:" и "handler:" и форматирует через formatStatusName
 * 
 * @param {string} eventName - Имя события (может быть с префиксами)
 * @param {function} formatStatusName - Функция форматирования имени статуса (Yt/normalizeString)
 * @returns {string} Отформатированное имя события
 */
export function formatTelemetryEventName(eventName, formatStatusName) {
  if (!eventName) return "";
  
  const cleaned = String(eventName)
    .replace(/^effect:/, "")
    .replace(/^handler:/, "");
  
  return formatStatusName(cleaned);
}

/**
 * Форматирует событие телеметрии в читаемую строку
 * Обрабатывает различные типы эффектов и событий
 * 
 * @param {object} event - Событие телеметрии
 * @param {function} getCombatantName - Функция получения имени бойца по ID
 * @param {function} formatPercent - Функция форматирования процентов
 * @param {function} formatEventName - Функция форматирования имени события
 * @returns {string|null} Отформатированная строка или null
 */
export function formatTelemetryEvent(event, getCombatantName, formatPercent, formatEventName) {
  if (!event || typeof event !== "object") return null;
  
  const eventType = event.event;
  if (!eventType || !eventType.startsWith("effect:")) return null;
  
  const owner = event.ownerId != null ? getCombatantName(event.ownerId) : null;
  const target = event.targetId != null ? getCombatantName(event.targetId) : null;
  const actor = event.actorId != null ? getCombatantName(event.actorId) : null;
  const attacker = event.attackerId != null ? getCombatantName(event.attackerId) : null;
  const defender = event.defenderId != null ? getCombatantName(event.defenderId) : null;
  
  switch (eventType) {
    case "effect:undead_god_bit_revive": {
      const hpPercent = event.hpPercent != null ? formatPercent(event.hpPercent, 0) : null;
      const duration = event.duration != null ? `${event.duration} ход.` : null;
      const details = [hpPercent ? `HP ${hpPercent}` : null, duration].filter(Boolean).join(", ");
      return `🧟 ${owner || "Существо"} воскресает${details ? ` (${details})` : ""}`;
    }
    
    case "effect:resurrect_god_flag": {
      const newType = event.newType ? `тип ${event.newType}` : null;
      return `⚑ ${owner || "Существо"} поднимается снова${newType ? ` (${newType})` : ""}`;
    }
    
    case "effect:memory_god_grave_respawn": {
      const hpPercent = event.hpPercent != null ? formatPercent(event.hpPercent, 0) : null;
      return `🪦 Тень памяти: ${getCombatantName(event.targetId)} возвращён${hpPercent ? ` (${hpPercent})` : ""}`;
    }
    
    case "effect:fragment_god_armor": {
      const totalBonus = event.totalBonus != null ? `+${event.totalBonus} DEF` : null;
      const fragments = event.fragments != null ? `${event.fragments} фрагм.` : null;
      const details = [totalBonus, fragments].filter(Boolean).join(", ");
      return `🧱 ${owner || "Существо"} усиливает броню${details ? ` (${details})` : ""}`;
    }
    
    case "effect:memory_dam_cast":
      return `🚫 ${owner || "Существо"} блокирует регенерацию врагов`;
    
    case "effect:memory_dam_block_regen": {
      const blocked = event.blocked != null ? `${event.blocked}` : null;
      return `🚫 Реген ${getCombatantName(event.targetId)} аннулирован${blocked ? ` (${blocked})` : ""}`;
    }
    
    case "effect:bit_plague_prime_cast": {
      const enemyDot = event.enemyDot != null ? `DoT ${event.enemyDot}` : null;
      const allyHot = event.allyHot != null ? `HoT ${event.allyHot}` : null;
      const details = [enemyDot, allyHot].filter(Boolean).join(", ");
      return `☣️ ${owner || "Существо"} заражает поле${details ? ` (${details})` : ""}`;
    }
    
    case "effect:bit_plague_prime_dot_tick":
      return `☣️ DoT: ${target || "Цель"} теряет ${event.dot ?? 0} HP`;
    
    case "effect:bit_plague_prime_hot_tick":
      return `💉 HoT: ${target || "Союзник"} восстанавливает ${event.hot ?? 0} HP`;
    
    case "effect:gateway_god_flood_cast": {
      const reroutes = event.reroutes != null ? `${event.reroutes} перенаправлений` : null;
      return `🔀 ${owner || "Существо"} запускает Gateway Flood${reroutes ? ` (${reroutes})` : ""}`;
    }
    
    case "effect:gateway_god_flood_redirect": {
      const newTarget = event.newTarget != null ? getCombatantName(event.newTarget) : null;
      return `🔁 Атака ${actor || "цели"} перенаправлена${newTarget ? ` → ${newTarget}` : ""}`;
    }
    
    case "effect:rate_god_limit_trigger": {
      const penalty = event.penalty != null ? `- ${event.penalty} SPD` : null;
      const skipTurns = event.skipTurns != null ? `${event.skipTurns} пропусков` : null;
      const details = [skipTurns, penalty].filter(Boolean).join(", ");
      return `📉 Rate Limit: ${getCombatantName(event.targetId)} наказан${details ? ` (${details})` : ""}`;
    }
    
    case "effect:rate_god_limit_skip": {
      const remaining = event.remaining != null ? `${event.remaining} осталось` : null;
      return `⏭️ Rate Limit: ${actor || "Существо"} пропускает ход${remaining ? ` (${remaining})` : ""}`;
    }
    
    case "effect:ddos_cathedral_cast": {
      const enemyDot = event.enemyDot != null ? `DoT ${event.enemyDot}` : null;
      const allyHot = event.allyHot != null ? `HoT ${event.allyHot}` : null;
      const details = [enemyDot, allyHot].filter(Boolean).join(", ");
      return `🌩️ ${owner || "Существо"} вызывает DDoS Cathedral${details ? ` (${details})` : ""}`;
    }
    
    case "effect:ddos_cathedral_dot_tick":
      return `🌩️ ${target || "Цель"} получает ${event.dot ?? 0} урона от DDoS`;
    
    case "effect:ddos_cathedral_hot_tick":
      return `✨ ${target || "Союзник"} лечится на ${event.hot ?? 0} от DDoS`;
    
    case "effect:stream_god_overflow_accumulate":
      return `💾 Stack Overflow: накоплено ${event.total ?? 0} (+${event.added ?? 0})`;
    
    case "effect:stream_god_overflow_ready":
      return `💾 Stack Overflow готов: ${event.stored ?? 0} урона после ${event.turns ?? 0} ходов`;
    
    case "effect:stream_god_overflow_release":
      return `💥 Stack Overflow высвобожден: бонус ${event.bonus ?? 0}`;
    
    case "effect:backpressure_god_applied": {
      const multiplier = event.multiplier != null ? `×${event.multiplier}` : null;
      return `🌊 Backpressure: ${getCombatantName(event.targetId)} душится${multiplier ? ` (${multiplier})` : ""}`;
    }
    
    case "effect:backpressure_god_reduce": {
      const multiplier = event.multiplier != null ? `×${event.multiplier}` : null;
      return `🌊 Backpressure снижает урон ${attacker || "атакующего"}${multiplier ? ` (${multiplier})` : ""}`;
    }
    
    case "effect:deadlock_apex_cast": {
      const allyBonusPercent = event.allyBonusPercent != null ? formatPercent(event.allyBonusPercent, 0) : null;
      return `🧊 Deadlock Apex: ${owner || "Существо"} ускоряет союзников${allyBonusPercent ? ` (+${allyBonusPercent})` : ""}`;
    }
    
    case "effect:deadlock_apex_skip":
      return `🧊 Deadlock Apex: ${getCombatantName(event.targetId)} пропускает ход`;
    
    case "effect:time_crown_cast": {
      const bonusPercent = event.bonusPercent != null ? formatPercent(event.bonusPercent, 0) : null;
      const enemySkips = event.enemySkips != null ? `${event.enemySkips} пропусков` : null;
      const details = [bonusPercent ? `бонус скорости ${bonusPercent}` : null, enemySkips].filter(Boolean).join(", ");
      return `⏱️ Time Crown активирован${details ? ` (${details})` : ""}`;
    }
    
    case "effect:time_crown_skip": {
      const skipsRemaining = event.skipsRemaining != null ? `${event.skipsRemaining} осталось` : null;
      return `⏭️ Time Crown: ${actor || "Существо"} пропускает ход${skipsRemaining ? ` (${skipsRemaining})` : ""}`;
    }
    
    case "effect:rom_eternity_cast": {
      const removed = event.removed != null ? `очищено ${event.removed}` : null;
      return `💾 ROM Eternity защищает союзников${removed ? ` (${removed})` : ""}`;
    }
    
    case "effect:fork_eruption_cast": {
      const extraActions = event.extraActions != null ? `+${event.extraActions} ход.` : null;
      const speedPenalty = event.speedPenalty != null ? `−${event.speedPenalty} SPD врагам` : null;
      const details = [extraActions, speedPenalty].filter(Boolean).join(", ");
      return `🍴 Fork Eruption: ${owner || "Существо"} перегружает поле${details ? ` (${details})` : ""}`;
    }
    
    case "effect:fork_eruption_extra_action": {
      const remaining = event.remaining != null ? `${event.remaining} осталось` : null;
      return `🍴 Доп. действие для ${actor || "союзника"}${remaining ? ` (${remaining})` : ""}`;
    }
    
    case "effect:identity_maelstrom_cast": {
      const allyEvadeBonus = event.allyEvadeBonus != null ? formatPercent(event.allyEvadeBonus, 0) : null;
      const enemyAccuracyPenalty = event.enemyAccuracyPenalty != null ? formatPercent(event.enemyAccuracyPenalty, 0) : null;
      const details = [allyEvadeBonus ? `уклонение +${allyEvadeBonus}` : null, enemyAccuracyPenalty ? `точность −${enemyAccuracyPenalty}` : null]
        .filter(Boolean)
        .join(", ");
      return `🎭 Identity Maelstrom меняет поле${details ? ` (${details})` : ""}`;
    }
    
    case "effect:identity_maelstrom_accuracy": {
      const penalty = event.penalty != null ? formatPercent(event.penalty, 0) : null;
      return `🎯 Потеря точности: ${attacker || "цель"} ${penalty ? `-${penalty}` : ""}`;
    }
    
    case "effect:identity_maelstrom_evade": {
      const totalBonus = event.totalBonus != null ? formatPercent(event.totalBonus, 0) : null;
      return `✨ Уклонение ${defender || "союзника"} повышено${totalBonus ? ` (${totalBonus})` : ""}`;
    }
    
    case "effect:mirror_universe_cast":
      return "🪞 Mirror Universe отражает эффекты";
    
    case "effect:sealed_orbit_cast":
      return "🛰️ Sealed Orbit активирован: союзники отражают дебаффы";
    
    case "effect:sealed_orbit_reflect_debuff":
      return `🛰️ Sealed Orbit отражает эффект на врага ${getCombatantName(event.enemyId)}`;
    
    case "effect:sealed_orbit_reveal_hidden":
      return `🔍 Скрытые статусы раскрыты у ${getCombatantName(event.enemyId)}`;
    
    case "effect:void_absolution_cast":
      return "🕳️ Void Absolution: союзники получили иммунитет к контролю";
    
    default: {
      const eventName = formatEventName(eventType);
      const participants = [];
      
      if (owner) participants.push(`инициатор ${owner}`);
      if (target) participants.push(`цель ${target}`);
      if (actor && !participants.includes(`инициатор ${actor}`)) participants.push(`участник ${actor}`);
      if (attacker && !participants.includes(`участник ${attacker}`)) participants.push(`атакующий ${attacker}`);
      if (defender && !participants.includes(`цель ${defender}`)) participants.push(`получатель ${defender}`);
      
      return participants.length ? `${eventName}: ${participants.join(", ")}` : eventName;
    }
  }
}

/**
 * Обрабатывает телеметрию боя и группирует события по раундам
 * @param {object} battle - Данные боя с полем telemetry
 * @param {array} panels - Панели существ
 * @param {function} formatTelemetryEvent - Функция форматирования события
 * @returns {object} Объект с событиями по раундам
 */
export function processTelemetryByRound(battle, panels, formatTelemetryEvent) {
  if (!battle || !Array.isArray(battle.telemetry)) return {};
  
  const panelsMap = new Map((panels || []).map((panel) => [String(panel.id), panel]));
  const eventsByRound = {};
  
  for (const event of battle.telemetry) {
    const round = event.round ?? 0;
    const formatted = formatTelemetryEvent(event, (id) => {
      const panel = panelsMap.get(String(id));
      return panel ? (panel.owner ? `${panel.owner} (${panel.name})` : panel.name) : null;
    });
    
    if (formatted) {
      if (!eventsByRound[round]) {
        eventsByRound[round] = [];
      }
      if (!eventsByRound[round].includes(formatted)) {
        eventsByRound[round].push(formatted);
      }
    }
  }
  
  return eventsByRound;
}

